import React, { FC, useMemo } from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import {
  Education,
  ProfessionalExperience,
  References,
  VolunteerExperience,
} from '../../../models/UserModels';
import moment from 'moment';
import { PdfHtml } from '../PdfHtml';
import Utils from '../../../utils/Utils';
import { CvTemplateProfile, ProfileActivity } from '../CvTemplateModal';
import config from '../../../config';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getRubikFont());
Font.registerHyphenationCallback((word: string) => {
  const urlIndex = word.indexOf('://');
  if (urlIndex !== -1) {
    const schema = word.substring(0, urlIndex + 3);
    const urlText = word
      .substring(urlIndex + 3)
      .split('/')
      .map((v, i) => (i === 0 ? `${schema}${v}` : v));
    return urlText
      .map((v, i) => (i === urlText.length - 1 ? v : `${v}/`))
      .flatMap((v) => v.match(/(.{1,18})/g))
      .filter((v) => !!v) as string[];
  }
  return (word.match(/(.{1,18})/g) as string[]).filter((v) => !!v) as string[];
});
const colors = (color: string) => {
  let color1;
  let color2;
  let color3;
  if (color === '#080808') {
    color1 = '#70797a';
    color2 = '#3b3a3a';
    color3 = '#595959';
  }
  if (color === '#042357') {
    color1 = '#1D3865';
    color2 = '#344869';
    color3 = '#5573a3';
  }
  if (color === '#022715') {
    color1 = '#4C6559';
    color2 = '#667B71';
    color3 = '#809c8e';
  }
  if (color === '#333434') {
    color1 = '#505050';
    color2 = '#6F6F6F';
    color3 = '#949292';
  }
  return { color1, color2, color3 };
};
const pageStyles = (props?: any) =>
  StyleSheet.create({
    body: {
      // flex: 1,
      // flexDirection: 'row',
      fontFamily: 'Rubik',
      padding: 24,
    },
    contentWrapper: {
      flex: 1,
      flexDirection: 'column',
    },
    iconText: {
      fontSize: 8,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    header: {
      width: 594,
    },
    headerName: {
      color: props?.color ? props.color : '#1A1A1A',
      fontSize: 24,
      fontWeight: 'medium',
    },
    headerStatement: {
      color: props?.color ? colors(props.color).color1 : '#2B2B2B',
      fontSize: 13,
      fontWeight: 'medium',
      marginTop: 2,
    },
    contacts: {
      marginTop: 4,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      flexWrap: 'wrap',
      fontWeight: 'semibold',
      fontSize: 8,
      width: 550,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      paddingRight: 12,
    },
    headerContactText: {
      fontSize: 8,
      marginLeft: 3,
      color: props?.color ? props.color : '#6F6F6F',
      textDecoration: 'none',
      fontWeight: 'normal',
    },
    summary: {
      marginVertical: 12,
    },
    titleText: {
      fontSize: 14,
      fontWeight: 'medium',
      color: props?.color ? props.color : '#1A1A1A',
      borderBottomWidth: 2,
      borderBottomColor: props?.color ? props?.color : '#6F6F6F',
    },
    designation: {
      fontSize: 12,
      fontWeight: 'normal',
      color: props?.color ? props.color : '#1A1A1A',
    },
    company: {
      fontSize: 10,
      marginTop: 2,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    durationCountryWrapper: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginTop: 2,
      marginBottom: 4,
      gap: 15,
    },
    country: {
      fontSize: 8,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
      marginLeft: 10,
    },
    duration: {
      fontSize: 8,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    description: {
      fontSize: 9,
      fontWeight: 'normal',
      color: props?.color ? props.color : '#6F6F6F',
    },
    dottedLine: {
      marginTop: 5,
      borderBottomWidth: 1,
      borderBottomColor: props?.color ? colors(props.color).color1 : '#E0E0E0',
      borderStyle: 'dashed',
      opacity: 0.6,
    },
    resultsWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
    },
    results: {
      fontSize: 10,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color2 : '#6F6F6F',
    },
    languages: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 30,
      marginTop: 5,
    },
    skillWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      columnGap: 15,
      rowGap: 5,
      marginTop: 5,
    },
    skills: {
      fontSize: 10,
      borderBottomWidth: 1,
      paddingHorizontal: 5,
      borderBottomColor: props?.color ? colors(props.color).color2 : '#E0E0E0',
      paddingBottom: 2,
      fontWeight: 'normal',
      color: props?.color ? props.color : '#6F6F6F',
    },
    refName: {
      fontSize: 12,
      fontWeight: 'normal',
      color: props?.color ? props.color : '#1A1A1A',
    },
    refDesignation: {
      fontSize: 10,
      marginTop: 2,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color3 : '#6F6F6F',
    },
    refOrganization: {
      fontSize: 10,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color3 : '#6F6F6F',
    },
    refContact: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginTop: 1,
      marginBottom: 4,
      gap: 15,
    },
    refContactText: {
      fontSize: 9,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
  });

// Create Document Component
type Props = {
  selectedColor?: string;
  profile: CvTemplateProfile;
};
const ExpCard: FC<{ value: ProfessionalExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginTop: 5 }}>
      <Text style={styles.designation}>{value.designation.value}</Text>
      <Text style={styles.company}>{value.company.value}</Text>
      <View style={styles.durationCountryWrapper}>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendar.png`}
          selectedColor={selectedColor}
        />
        <IconTextView
          text={value.country || ''}
          img={`${config.frontendUrl}/locationPin.png`}
          selectedColor={selectedColor}
        />
      </View>
      {value.description && <DescriptionText text={value.description} />}
      {!isLast && <View style={styles.dottedLine} />}
    </View>
  );
};
const VolCard: FC<{ value: VolunteerExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginTop: 5 }}>
      <Text style={styles.designation}>{value.organization.value}</Text>
      <Text style={styles.company}>{value.role.value}</Text>
      <View style={styles.durationCountryWrapper}>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendar.png`}
          selectedColor={selectedColor}
        />
      </View>
      {value.description && <DescriptionText text={value.description} />}
      {!isLast && <View style={styles.dottedLine} />}
    </View>
  );
};
const EduCard: FC<{ value: Education; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const { type, results, institute, achievements, title, description } = value;
  const duration = useMemo(() => {
    const from = moment(value.period?.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period?.to);
    const toStr = value?.period?.to
      ? to.format('MMM YYYY')
      : type === 'Certification'
      ? 'None'
      : 'Present';
    return [`${fromStr} - ${toStr}`, fromStr, toStr];
  }, [type, value?.period?.from, value?.period?.to]);
  return (
    <View wrap={false} style={{ marginTop: 5 }}>
      <View>
        {type === 'Certification' ? (
          <>
            <Text style={styles.designation}>{`${results[0]?.subject?.value ?? type}`}</Text>
          </>
        ) : type === 'GCE A/L' || type === 'GCE O/L' ? (
          <>
            <Text style={styles.designation}>{type}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result}),` : ','
                }`}</Text>
              ))}
            </View>
          </>
        ) : type === 'Other' ? (
          <>
            <Text style={styles.designation}>{title ?? ''}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result})` : ''
                }`}</Text>
              ))}
            </View>
          </>
        ) : (
          results.map((r, i) => (
            <Text key={i} style={styles.designation}>{`${r.subject?.value}${
              r.result ? `, ${r.result}` : ''
            }`}</Text>
          ))
        )}
      </View>
      {institute && <Text style={styles.company}>{institute.value}</Text>}
      {type !== 'Certification' && (
        <View style={styles.durationCountryWrapper}>
          <IconTextView
            text={duration[0]}
            img={`${config.frontendUrl}/calendar.png`}
            selectedColor={selectedColor}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationPin.png`}
            selectedColor={selectedColor}
          />
        </View>
      )}
      {type === 'Certification' && value?.period && (
        <View style={styles.durationCountryWrapper}>
          <IconTextView
            text={duration[1]}
            img={`${config.frontendUrl}/calendar.png`}
            selectedColor={selectedColor}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationPin.png`}
            selectedColor={selectedColor}
          />
        </View>
      )}
      {description && type === 'Other' && <DescriptionText text={description} />}
      {achievements && (
        <View style={styles.description}>
          <PdfHtml level={2} fontSize="9" fontFamily="Rubik" lineHeight={1.3}>
            {achievements.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
      {!isLast && <View style={styles.dottedLine} />}
    </View>
  );
};
const ActivitySocityView: FC<{
  value: ProfileActivity[];
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const activity = value ?? [];
  const data = activity.map((item, index) => {
    const isLastActivity = index === activity.length - 1;
    return (
      <View wrap={false} key={index}>
        {index === 0 && (
          <Text style={[styles.designation, { marginTop: 5 }]}>Activities & Societies</Text>
        )}
        {item.institute && item.description && (
          <Text style={[styles.company, { marginTop: 5 }]}>{item.institute}</Text>
        )}

        {item.description.map((v) => {
          if (v) {
            return (
              <View style={{ marginTop: 3 }} key={v}>
                <DescriptionText text={v} />
              </View>
            );
          }
        })}
        {!isLastActivity && <View style={styles.dottedLine} />}
      </View>
    );
  });
  return <>{data}</>;
};
const ContactDetailView: FC<{
  img: string;
  value: string;
  selectedColor?: string;
  isUrl?: boolean;
  title?: string;
  type?: 'email';
  iconWidth?: number;
  iconHeight?: number;
  width?: string;
}> = ({ selectedColor, img, value, isUrl, title, type, iconHeight, iconWidth, width }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.row}>
      <Image
        src={img}
        style={{
          width: iconWidth ?? 10,
          height: iconHeight ?? 10,
          color: selectedColor,
          padding: 1,
        }}
      />
      <View style={{ paddingVertical: 2 }}>
        {isUrl ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : type && type === 'email' ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : (
          <Text style={styles.headerContactText}>{value}</Text>
        )}
      </View>
    </View>
  );
};
const ReferenceView: FC<{
  value: References;
  selectedColor?: string;
  isLast?: boolean;
}> = ({ value, selectedColor, isLast }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View wrap={false} style={{ marginTop: 5 }}>
      <Text style={styles.refName}>
        {value.firstName} {value.lastName}
      </Text>
      <Text style={styles.refDesignation}>{value.designation}</Text>
      <Text style={styles.refOrganization}>{value.organization}</Text>
      <View style={styles.refContact}>
        <Text style={styles.refContactText}>{value.email}</Text>
        <Text style={styles.refContactText}>{value.mobile}</Text>
      </View>
      {!isLast && <View style={styles.dottedLine} />}
    </View>
  );
};

const TitleText: FC<{ title: string; selectedColor?: string }> = ({ title, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return <Text style={styles.titleText}>{title.toUpperCase()}</Text>;
};

const DescriptionText: FC<{ text: string }> = ({ text }) => {
  return (
    <PdfHtml level={2} fontSize="9" fontFamily="Rubik" lineHeight={1.3}>
      {text.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
    </PdfHtml>
  );
};
const IconTextView: FC<{
  text: string;
  img: string;
  selectedColor?: string;
  iconWidth?: number;
  iconHeight?: number;
}> = ({ text, img, selectedColor, iconWidth, iconHeight }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.row}>
      <Image
        src={img}
        style={{
          width: iconWidth ?? 10,
          height: iconHeight ?? 10,
          marginRight: 2,
          color: selectedColor,
          padding: 1,
          opacity: 0.6,
        }}
      />
      <Text style={styles.iconText}>{text}</Text>
    </View>
  );
};

const Template1 = (props: Props) => {
  const styles = useMemo(() => pageStyles({ color: props.selectedColor }), [props.selectedColor]);
  const { profile } = props;

  const isShowActivity = useMemo(() => {
    const temp: Education[] = [...(profile?.education ?? [])];
    const found = temp.find((v) => v.description);
    return !!found;
  }, [profile?.education]);
  const isShowSummery = useMemo(() => {
    return !!(profile?.personalStatement || profile?.desiredJobTitle);
  }, [profile?.desiredJobTitle, profile?.personalStatement]);
  return (
    <Document>
      <Page size={'A4'} break={true} style={styles.body}>
        <View style={styles.contentWrapper}>
          {/* HEADER SECTION */}
          <View style={styles.header}>
            <Text style={styles.headerName}>{profile.name.toUpperCase()}</Text>
            <Text style={styles.headerStatement}>{profile.personalStatementHeader}</Text>
            <View style={styles.contacts}>
              {profile?.mobile && (
                <ContactDetailView
                  value={profile.mobile}
                  img={`${config.frontendUrl}/phone.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.residentialAddress && (
                <ContactDetailView
                  value={profile.residentialAddress}
                  img={`${config.frontendUrl}/locationPin.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.linkedInUrl && (
                <ContactDetailView
                  isUrl={true}
                  title={'Linkedin.com'}
                  value={profile.linkedInUrl}
                  img={`${config.frontendUrl}/linkedin.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.userId && (
                <ContactDetailView
                  isUrl={true}
                  title={'Careers360.com'}
                  value={`${config.frontendUrl}/profile/${profile.userId}`}
                  img={`${config.frontendUrl}/careers360.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.email && (
                <ContactDetailView
                  title={profile.email}
                  type={'email'}
                  value={`mailto:${profile.email}`}
                  img={`${config.frontendUrl}/email.png`}
                  selectedColor={props.selectedColor}
                />
              )}
            </View>
          </View>

          {/* SUMMARY SECTION */}
          {isShowSummery && (
            <View style={styles.summary}>
              <TitleText title="Summary" selectedColor={props.selectedColor} />
              {profile.personalStatement && (
                <View style={{ marginTop: 5 }}>
                  <DescriptionText text={profile.personalStatement} />
                </View>
              )}
            </View>
          )}

          {/* EXPERIENCE SECTION */}
          {profile.experience && profile.experience.length > 0 && (
            <View style={{ marginBottom: 12 }}>
              <TitleText title="Experience" selectedColor={props.selectedColor} />
              {profile.experience.map((exp, index) => (
                <ExpCard
                  key={index}
                  value={exp}
                  selectedColor={props.selectedColor}
                  isLast={profile.experience ? index === profile.experience.length - 1 : false}
                />
              ))}
            </View>
          )}
          {profile.education && profile.education.length > 0 && (
            <View style={{ marginBottom: 12 }}>
              <TitleText title="Education" selectedColor={props.selectedColor} />
              {profile.education.map((edu, index) => (
                <EduCard
                  key={index}
                  value={edu}
                  selectedColor={props.selectedColor}
                  isLast={profile.education ? index === profile.education.length - 1 : false}
                />
              ))}
            </View>
          )}

          {/* ACHIEVEMENTS SECTION */}
          {isShowActivity && (
            <View style={{ marginBottom: 12 }}>
              <TitleText title="Achievements" selectedColor={props.selectedColor} />
              <ActivitySocityView value={profile.activities} selectedColor={props.selectedColor} />
            </View>
          )}
          {/* LANGUAGES SECTION */}
          {profile?.languages && profile.languages.length > 0 && (
            <View wrap={false} style={{ marginBottom: 12 }}>
              <TitleText title="Languages" selectedColor={props.selectedColor} />
              <View style={styles.languages}>
                {profile.languages.map((lang, index) => (
                  <View key={index}>
                    <Text style={styles.designation}>{lang.language}</Text>
                    <Text style={styles.company}>{lang.fluency}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* VOLUNTEER SECTION */}
          {profile?.volunteering && profile.volunteering.length > 0 && (
            <View style={{ marginBottom: 12 }}>
              <TitleText title="Volunteering" selectedColor={props.selectedColor} />
              {profile.volunteering.map((vol, index) => (
                <VolCard
                  key={index}
                  value={vol}
                  selectedColor={props.selectedColor}
                  isLast={profile.volunteering ? index === profile.volunteering.length - 1 : false}
                />
              ))}
            </View>
          )}

          {/* SKILLS SECTION */}
          {profile?.skills && profile.skills.length > 0 && (
            <View wrap={false} style={{ marginBottom: 15 }}>
              <TitleText title="Skills" selectedColor={props.selectedColor} />
              <View style={styles.skillWrapper}>
                {profile.skills.map((skill, index) => (
                  <Text key={index} style={styles.skills}>
                    {skill.value}
                  </Text>
                ))}
              </View>
            </View>
          )}

          {/* REFERENCES SECTION */}
          {profile?.reference && profile.reference.length > 0 && (
            <View>
              <TitleText title="References" selectedColor={props.selectedColor} />
              {profile.reference.map((ref, index) => (
                <ReferenceView
                  key={index}
                  value={ref}
                  selectedColor={props.selectedColor}
                  isLast={profile.reference ? index === profile.reference.length - 1 : false}
                />
              ))}
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};
export default Template1;
